import { useEffect, useRef } from "react";
import { AnnotationEditor } from "@markerjs/markerjs-ui";
import { getPresignedImageUrlForFile, uploadFile } from "@/network/network.jsx";
import { toast } from "sonner";
import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import type { ISwivuProject } from "@/types/SwivuProject";
import type { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import type { EntityRecord } from "@/types/entity";

interface UploadResponse {
  id: string | number;
  fileName: string;
}

interface CleanupRef {
  containerElement: HTMLDivElement | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  saveHandler: ((event: any) => Promise<void>) | null;
  closeHandler: (() => void) | null;
}

interface ImageAnnotationEditorProps {
  entityRecord: EntityRecord;
  project: ISwivuProject;
  fileToAnnotate: FormattedFile;
  onClose: () => void;
}

/**
 * Adjusts the sizing of the annotation editor image
 * Sets image to auto width and 80vh height for optimal viewing
 */
const adjustImageSizing = () => {
  try {
    const annotationEditor = document.querySelector("mjsui-annotation-editor") as HTMLElement & { shadowRoot?: ShadowRoot };
    if (!annotationEditor?.shadowRoot) return;

    const markerArea = annotationEditor.shadowRoot.querySelector("mjs-marker-area") as HTMLElement & { shadowRoot?: ShadowRoot };
    if (!markerArea?.shadowRoot) return;

    const imgElement = markerArea.shadowRoot.querySelector("img");
    if (imgElement) {
      imgElement.style.width = "auto";
      imgElement.style.height = "80vh";
      imgElement.style.maxWidth = "100%";
      imgElement.style.maxHeight = "100%";
    }
  } catch (error) {
    // Silently fail - non-critical styling adjustment
  }
};

/**
 * Applies custom styles to a button element
 */
const styleButton = (button: HTMLElement, backgroundColor: string) => {
  button.style.backgroundColor = backgroundColor;
  button.style.color = 'white';
  button.style.fontWeight = '600';
  button.style.borderRadius = '6px';
  button.style.border = 'none';
  button.style.cursor = 'pointer';
  button.style.transition = 'background-color 0.2s ease';
};

/**
 * Styles the annotation toolbar buttons (OK and Close)
 */
const styleAnnotationButtons = () => {
  const annotationEditor = document.querySelector("mjsui-annotation-editor") as HTMLElement & { shadowRoot?: ShadowRoot };
  if (annotationEditor?.shadowRoot) {
    const okButton = annotationEditor.shadowRoot.querySelector('[title="OK"]') as HTMLElement;
    const closeButton = annotationEditor.shadowRoot.querySelector('[title="Close"]') as HTMLElement;

    if (okButton) styleButton(okButton, '#0096ff');
    if (closeButton) styleButton(closeButton, '#374151');
  }
};

/**
 * Uploads the annotated image file
 */
const uploadAnnotatedFile = async (dataUrl: string, fileToAnnotate: FormattedFile): Promise<UploadResponse> => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();

  let file: File;
  if (fileToAnnotate.isCCImage) {
    const lastDotIndex = fileToAnnotate.originalImageUrl?.lastIndexOf(".") || -1;
    const extension = fileToAnnotate.originalImageUrl?.slice(lastDotIndex) || "";
    file = new File([blob], `${fileToAnnotate.name}${extension}`, { type: blob.type });
  } else {
    file = new File([blob], fileToAnnotate.name, { type: blob.type });
  }

  const { data: uploadData } = await uploadFile(file, file.name);
  return uploadData as UploadResponse;
};

/**
 * ImageAnnotationEditor Component
 * Provides an interface for annotating images using MarkerJS
 *
 * @param project - The project containing the board files store
 * @param entityRecord - The entityRecord containing the board files store
 * @param fileToAnnotate - The file object to be annotated
 * @param onClose - Callback function when editor closes
 */
const ImageAnnotationEditor = ({ entityRecord, project, fileToAnnotate, onClose }: ImageAnnotationEditorProps) => {
  const cleanupRef = useRef<CleanupRef>({
    containerElement: null,
    saveHandler: null,
    closeHandler: null
  });

  useEffect(() => {
    // Validate required props
    if (!project?.boardFilesStore) {
      toast.error("Unable to initialize editor. Missing project data.");
      onClose?.();
      return;
    }

    if (!fileToAnnotate?.originalImageUrl || !fileToAnnotate?.id || !fileToAnnotate?.name) {
      toast.error("Unable to initialize editor. Missing file data.");
      onClose?.();
      return;
    }

    /**
     * Initializes and displays the annotation editor
     */
    const initializeEditor = async () => {
      try {
        // Create container elements
        const topContainerElement = document.createElement("div");
        topContainerElement.classList.add("swivu-annotation-editor-top-container");

        const containerElement = document.createElement("div");
        containerElement.classList.add("swivu-annotation-editor");

        topContainerElement.appendChild(containerElement);

        const floatRoot = document.querySelector(".b-float-root");
        if (!floatRoot) {
          throw new Error("Float root element not found");
        }

        floatRoot.after(topContainerElement);
        cleanupRef.current.containerElement = topContainerElement;

        // Fetch and prepare image...
        // there were some cors error if we get image usng fileRecord.originalImageUrl that is why we creating this
        // separate endpoint to fetch the image url only
        let presignedImageUrl: string;
        if (fileToAnnotate.isCCImage) {
          presignedImageUrl = fileToAnnotate.originalImageUrl || "";
        } else {
          const response = await getPresignedImageUrlForFile(fileToAnnotate.id);
          presignedImageUrl = response.data.url;
        }

        // Create and configure target image
        const targetImg = document.createElement("img");
        targetImg.crossOrigin = "anonymous";
        targetImg.src = presignedImageUrl;

        // Initialize marker editor
        const markerEditor = new AnnotationEditor();
        markerEditor.targetImage = targetImg;
        containerElement.appendChild(markerEditor);

        // Apply sizing adjustments
        adjustImageSizing();

        // Style toolbar buttons after shadow DOM renders
        setTimeout(styleAnnotationButtons, 300);

        // Setup save handler
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleSave = async (event: any) => {
          try {
            toast.loading("Uploading annotated image...", { id: "file-upload" });

            const dataUrl = event.detail?.dataUrl as string | undefined;
            if (!dataUrl) {
              throw new Error("No image data received from editor");
            }

            const uploadData = await uploadAnnotatedFile(dataUrl, fileToAnnotate);

            if (!uploadData?.id) {
              throw new Error("Invalid upload response");
            }

            // Create new annotated image record
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let annotatedImageRecord: any;
            if (!fileToAnnotate.isCCImage) {
              const fileInstance = project.boardFilesStore.getById(fileToAnnotate.id);
              if (!fileInstance) {
                throw new Error("Original file not found in store");
              }
              annotatedImageRecord = fileInstance.copy();
              annotatedImageRecord.documentId = uploadData.id;
            } else {
              const { size, order } = fileToAnnotate?.record || {};
              annotatedImageRecord = project.boardFilesStore.createRecord({
                name: uploadData.fileName,
                canEdit: GRANTS.grant_one,
                canView: GRANTS.grant_one,
                size,
                [entityRecord.entityType]: entityRecord.id,
                documentId: uploadData.id,
                order
              });
            }

            // Set order to latest (highest order + 1)
            const maxOrder = project.boardFilesStore.records.reduce((max, record) => Math.max(max, record.order || 0), 0);
            annotatedImageRecord.order = maxOrder + 1;

            // Mark as annotated image
            annotatedImageRecord.isAnnotated = true;

            project.boardFilesStore.add(annotatedImageRecord);

            toast.success("Successfully uploaded annotated image", { id: "file-upload" });

            // Cleanup and close
            cleanupEditor();
            onClose?.();
          } catch (error) {
            console.error("File upload error:", error);
            toast.error((error as Error).message || "Failed to upload. Please try again.", { id: "file-upload" });
          }
        };

        // Setup close handler with confirmation
        const handleClose = () => {
          const confirmed = window.confirm(
            "Are you sure you want to close the editor? Any unsaved changes will be lost."
          );

          if (confirmed) {
            cleanupEditor();
            onClose?.();
          }
        };

        // Store handlers for cleanup
        cleanupRef.current.saveHandler = handleSave;
        cleanupRef.current.closeHandler = handleClose;

        // Attach event listeners
        markerEditor.addEventListener("editorsave", handleSave);
        markerEditor.addEventListener("editorclose", handleClose);
      } catch (error) {
        console.error("Error initializing annotation editor:", error);
        toast.error((error as Error).message || "Failed to initialize editor. Please try again.");
        cleanupEditor();
        onClose?.();
      }
    };

    /**
     * Cleans up all resources used by the editor
     */
    const cleanupEditor = () => {
      // Remove container element
      if (cleanupRef.current.containerElement) {
        cleanupRef.current.containerElement.remove();
        cleanupRef.current.containerElement = null;
      }

      // Clear handler references
      cleanupRef.current.saveHandler = null;
      cleanupRef.current.closeHandler = null;
    };

    // Initialize the editor
    initializeEditor();

    // Cleanup on unmount
    return cleanupEditor;
  }, [project, fileToAnnotate, onClose, entityRecord]);

  return null;
};

export default ImageAnnotationEditor;
